import { tokens } from "@core/theme/tokens";
import {
  DialogActions,
  DialogHeader,
  Modal,
  PortalButton,
  useToast,
} from "@portal/common";
import { reportError } from "@portal/common/errorMessage";
import { formatShortDate } from "../vehicles/dateFormat";
import { PROGRESS_ERROR_MESSAGES, type ProgressDetail } from "./progressApi";
import { useProgressMutations } from "./progressQueries";
import { Ring } from "./Ring";

// SUB-001 paso 1 (resolución humana 2026-09-07): el modal canónico
// «Validar ganador» pasa a ser el de validación. Conserva el detalle de las
// compras que sustentan la meta —referido, fecha y quién registró—, con
// acción primaria «Validar la meta» y SIN el checkbox de entrega, que
// pertenece al modal de PrizeDeliveryModal.
export function GoalValidationModal({
  progress,
  onClose,
}: {
  progress: ProgressDetail;
  onClose: () => void;
}) {
  const toast = useToast();
  const mutations = useProgressMutations();

  const submit = () => {
    mutations.validateGoal.mutate(progress.id, {
      onSuccess: () => {
        toast.show("Meta validada.");
        onClose();
      },
      onError: (error) => reportError(toast, error, PROGRESS_ERROR_MESSAGES),
    });
  };

  return (
    // Revisión en vivo 2026-09-22, puntos #3/#4A/#15: las tres regiones del
    // `Modal`. El título y su bajada suben al encabezado fijo, las acciones
    // bajan al pie fijo (`DialogActions`, que ya trae el "Cancelar"
    // secundario en vez del `.btn-link` crudo que había acá) y el cuerpo —la
    // evidencia de compras, que es lo único que puede crecer— queda como la
    // única región con scroll. Los márgenes que separaban título, bajada y
    // botones se borran: ese aire lo ponen ahora las regiones del `Modal`.
    <Modal
      label="Validar la meta"
      width={620}
      onClose={onClose}
      header={
        // Punto #21 (revisión en vivo 2026-09-22): encabezado común; la
        // bajada sigue debajo, con el aire que antes daba el margen del título.
        <>
          <DialogHeader
            eyebrow="Gran premio · Avances"
            title="Validar la meta"
          />
          <div
            style={{
              font: `400 12px/1.55 ${tokens.font.ui}`,
              color: tokens.text.tertiary,
              marginTop: 5,
            }}
          >
            {progress.member.legalName} alcanzó la meta. Revisa la evidencia
            antes de validar.
          </div>
        </>
      }
      footer={
        <DialogActions onCancel={onClose}>
          <PortalButton
            type="button"
            tone="primary"
            size="small"
            onClick={submit}
            disabled={mutations.validateGoal.isPending}
          >
            {mutations.validateGoal.isPending
              ? "Validando…"
              : "Validar la meta"}
          </PortalButton>
        </DialogActions>
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 20,
          padding: 16,
          borderRadius: tokens.radius.sm,
          border: `1px solid ${tokens.surface.line}`,
          marginBottom: 16,
        }}
      >
        <Ring n={progress.progress} goal={progress.goal} size={96} />
        <div
          style={{
            font: `400 12.5px/1.6 ${tokens.font.ui}`,
            color: tokens.text.secondary,
          }}
        >
          Validar registra tu usuario y este momento como evidencia de la
          decisión.
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${tokens.surface.line}`,
          borderRadius: tokens.radius.sm,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "14px 16px 10px" }}>
          <span className="label">Compras que sustentan la meta</span>
        </div>
        {progress.purchasesInWindow.map((purchase) => (
          <div
            key={`${purchase.referralId}-${purchase.date}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 16px",
              borderTop: `1px solid ${tokens.surface.line}`,
              font: `400 12.5px ${tokens.font.ui}`,
            }}
          >
            <span>{purchase.referralName}</span>
            <span style={{ color: tokens.text.tertiary }}>
              {formatShortDate(purchase.date)}
              {purchase.registeredBy ? ` · ${purchase.registeredBy}` : ""}
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
